import React, { useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import './RegionalBarChart.css'; // Import CSS for styling

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const RegionBarChart = ({ results }) => {
    const [selectedRegion, setSelectedRegion] = useState('All Regions');

    // Helper function to ensure bars with 0 values are visible
    const ensureVisibleBars = (data) => data.map(value => (value === 0 ? 0.01 : value));

    // Extract region names and data
    const regions = Object.keys(results);
    const filteredResults = selectedRegion === 'All Regions' ? results : { [selectedRegion]: results[selectedRegion] };

    const passData = ensureVisibleBars(Object.keys(filteredResults).map(region => filteredResults[region].Pass));
    const improvementRequiredData = ensureVisibleBars(Object.keys(filteredResults).map(region => filteredResults[region].Improvement_Required));
    const significantImprovementRequiredData = ensureVisibleBars(Object.keys(filteredResults).map(region => filteredResults[region].Significant_Improvement_Required));
    const allValues = [
        ...Object.keys(filteredResults).map(region => filteredResults[region].Pass),
        ...Object.keys(filteredResults).map(region => filteredResults[region].Improvement_Required),
        ...Object.keys(filteredResults).map(region => filteredResults[region].Significant_Improvement_Required)
    ];
    const maxValue = Math.max(...allValues);
    const yAxisMax = maxValue + 10;
    // Chart data
    const data = {
        labels: Object.keys(filteredResults),
        datasets: [
            {
                label: 'Pass',
                data: passData,
                backgroundColor: '#4CAF50', // Green
            },
                
            {
                label: 'Improvement Required',
                data: improvementRequiredData,
                backgroundColor: '#FFC107', // Yellow
            },
            {
                label: 'Significant Improvement Required',
                data: significantImprovementRequiredData,
                backgroundColor: '#F44336', // Red
            },
        ],
    };

    // Chart options
    const options = {
        responsive: true, // Ensure the chart is responsive
        maintainAspectRatio: false, // Allow the chart to stretch to the container's height
        plugins: {
            legend: {
                position: 'top',
            },
            title: {
                display: true,
                text: selectedRegion === 'All Regions' ? 'Region Results' : `Results for ${selectedRegion}`,
            },
        },
        scales: {
            x: {
                ticks: {
                    autoSkip: false, // Prevent skipping of labels
                    maxRotation: 45, // Rotate labels for better readability
                    minRotation: 0,
                },
                grid: {
                    display: true, // Enable grid lines
                    drawBorder: true, // Draw the border line
                    drawOnChartArea: false, // Only draw lines between categories
                    drawTicks: true, // Draw tick marks
                    color: '#ddd', // Set the color of the grid lines
                },
                grouped: true, // Ensure bars are grouped properly

            },
            y: {
                beginAtZero: true, 
                max: yAxisMax,
                // Ensure the y-axis starts at 0
                grid: {
                    display: true, // Enable grid lines for the y-axis
                    color: '#eee', // Set a lighter color for the y-axis grid lines
                },
            },
        },
        categoryPercentage: 0.8, // Adjust spacing between categories
        barPercentage: 0.5, // Increase spacing between bars within a category
        barThickness: undefined, // Allow dynamic bar sizing
        maxBarThickness: undefined, // Allow dynamic bar sizing
        
    };

    return (
        <div className="dashboard-container">
            <h2 className="dashboard-title">Regional Bar Chart</h2>
            <div className="filter-container">
                <label htmlFor="region-select">Filter by Region:</label>
                <select
                    id="region-select"
                    value={selectedRegion}
                    onChange={(e) => setSelectedRegion(e.target.value)}
                >
                    <option value="All Regions">All Regions</option>
                    {regions.map((region) => (
                        <option key={region} value={region}>
                            {region}
                        </option>
                    ))}
                </select>
            </div>
            <div className="chart-container">
                <Bar data={data} options={options} />
            </div>
        </div>
    );
};

export default RegionBarChart;